import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';

import WorkOrderContext, {
  WorkOrderActionTypes,
} from '@atom/components/workOrderDetail/WorkOrderContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { DatePicker, DateTimePicker } from '@atom/mui';
import { Task } from '@atom/types/task';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';
import { showWorkDateTime } from '@atom/utilities/workPreferenceUtilities';

import classes from './taskDetailView.module.css';

const DEBOUNCE_TIME = 1000;

const TaskCompletedDate = () => {
  const { workOrderDetail, task, dispatch } = useContext(WorkOrderContext);
  const [completionDate, setCompletionDate] = useState<Date>(
    task.completionDate ? new Date(task.completionDate) : null,
  );

  const [taskUpdate] = useMutation(TASK_UPDATE);

  useEffect(() => {
    setCompletionDate(
      task.completionDate && task.isCompleted
        ? new Date(task.completionDate)
        : null,
    );
  }, [task.completionDate, task.isCompleted]);

  const setTask = (updatedTask: Task) => {
    dispatch({
      type: WorkOrderActionTypes.SET_TASK,
      data: {
        ...updatedTask,
      },
    });
  };

  const handleSubmit = async (date: Date) => {
    const res = await taskUpdate({
      variables: {
        input: {
          id: task.id,
          workOrderId: workOrderDetail.id,
          completionDate: showWorkDateTime(workOrderDetail)
            ? date.valueOf()
            : convertDateToMillisGMTMidday(date),
        },
      },
    });

    setTask(res?.data?.taskUpdate);
  };

  const debouncedHandleSubmit = useCallback(
    debounce(handleSubmit, DEBOUNCE_TIME),
    [task.id, workOrderDetail.id],
  );

  const onChange = (date?: Date) => {
    setCompletionDate(date);

    if (!date) {
      return;
    } else if (!Number.isNaN(date.valueOf())) {
      debouncedHandleSubmit(date);
    }
  };

  const onBlur = () => {
    if (!completionDate) {
      setCompletionDate(
        task.completionDate && task.isCompleted
          ? new Date(task.completionDate)
          : null,
      );
    }
  };

  const isDisabled =
    !task.isCompleted ||
    workOrderDetail.isClosed ||
    doesNotHaveRolePermissions(ROLE_SETS.MANAGER);

  return (
    <div
      className={clsx(
        classes.timeRowItem,
        classes.input,
        showWorkDateTime(workOrderDetail) && classes.dateTimeInput,
      )}
    >
      <div className={classes.label}>Completed Date</div>
      <div className={classes.timePickerContainer} id="completedDate">
        {showWorkDateTime(workOrderDetail) ? (
          <DateTimePicker
            value={completionDate}
            onChange={onChange}
            disabled={isDisabled}
            onBlur={onBlur}
            data-cy="taskCompletedDateInput"
          />
        ) : (
          <DatePicker
            value={completionDate}
            onChange={onChange}
            disabled={isDisabled}
            onBlur={onBlur}
            data-cy="taskCompletedDateInput"
          />
        )}
      </div>
    </div>
  );
};

export default TaskCompletedDate;
